import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Alert from "../../../Common/Alert";
import DatatableFunc from "../../../Common/DatatableFunc";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
// import { LoaderComponent } from "../../../Common/LoaderComponent";
import ReactDataTable from "../../../Common/ReactDataTable";

const List = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [receiveData, setReceiveData] = useState([]);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);

  // const fetchData = () => {
  //   Http.get("/cash-count").then((res) => {
  //     setReceiveData(res.data);
  //   });
  // };
  const fetchData = async () => {
    setLoading(true); // Start loading

    try {
      const res = await Http.get(`/cash-count`);
      setReceiveData(res.data);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false); // Stop loading regardless of the outcome
    }
  };

  useEffect(() => {
    DatatableFunc();
    fetchData();
  }, []);

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const deleteData = (id, deleteApiUrl, showMsg) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        Http.delete("/" + deleteApiUrl + "/" + id + "/cash-count").then(
          (res) => {
            setReceiveData(res.data);
            Swal.fire("Deleted!", showMsg + " has been deleted.", "success");
          }
        );
      }
    });
  };

  // Format currency values for better display
  const formatCurrency = (value) => {
    if (!value || value === 0) return "0.00";
    return parseFloat(value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Format date for better display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  // Custom body templates for better data presentation
  const dateBodyTemplate = (rowData) => {
    return (
      <span className="text-primary fw-bold">{formatDate(rowData.date)}</span>
    );
  };

  // const currencyBodyTemplate = (field) => (rowData) => {
  //   const value = rowData[field];
  //   return (
  //     <span
  //       className={`badge ${
  //         value > 0 ? "bg-success" : "bg-secondary"
  //       } text-white`}
  //     >
  //       {formatCurrency(value)}
  //     </span>
  //   );
  // };

  const currencyBodyTemplate = (field) => {
    const Component = (rowData) => {
      const value = rowData[field];
      return (
        <span
          className={`badge ${
            value > 0 ? "bg-success" : "bg-secondary"
          } text-white`}
        >
          {formatCurrency(value)}
        </span>
      );
    };

    // Set the display name for the component
    Component.displayName = `CurrencyBodyTemplate(${field})`;

    return Component;
  };

  const totalBodyTemplate = (rowData) => {
    return (
      <span className="fw-bold text-success fs-6">
        ৳ {formatCurrency(rowData.total_amount)}
      </span>
    );
  };

  const suspenseBodyTemplate = (rowData) => {
    return (
      <span
        className={`badge ${
          rowData.suspense > 0 ? "bg-warning text-dark" : "bg-light text-muted"
        }`}
      >
        {formatCurrency(rowData.suspense)}
      </span>
    );
  };

  const actionTemplate = (item, deleteData) => {
    return (
      <div className="d-flex gap-1">
        <button
          className="btn btn-info btn-sm"
          onClick={() => navigate(`/view-cash-count/${item.id}`)}
          title="View Details"
        >
          <i className="fa fa-eye"></i>
        </button>
        <button
          className="btn btn-danger btn-sm"
          onClick={() => deleteData(item.id, "transaction", "cash-count")}
          title="Delete Cash Count"
        >
          <i className="fa fa-trash"></i>
        </button>
      </div>
    );
  };

  const cols = [
    {
      field: "date",
      header: "Date",
      body: dateBodyTemplate,
      sortable: true,
    },
    {
      field: "thousand_total",
      header: "৳1000",
      body: currencyBodyTemplate("thousand_total"),
      sortable: true,
    },
    {
      field: "five_hundred_total",
      header: "৳500",
      body: currencyBodyTemplate("five_hundred_total"),
      sortable: true,
    },
    {
      field: "two_hundred_total",
      header: "৳200",
      body: currencyBodyTemplate("two_hundred_total"),
      sortable: true,
    },
    {
      field: "one_hundred_total",
      header: "৳100",
      body: currencyBodyTemplate("one_hundred_total"),
      sortable: true,
    },
    {
      field: "fifty_total",
      header: "৳50",
      body: currencyBodyTemplate("fifty_total"),
      sortable: true,
    },
    {
      field: "twenty_total",
      header: "৳20",
      body: currencyBodyTemplate("twenty_total"),
      sortable: true,
    },
    {
      field: "ten_total",
      header: "৳10",
      body: currencyBodyTemplate("ten_total"),
      sortable: true,
    },
    {
      field: "five_total",
      header: "৳5",
      body: currencyBodyTemplate("five_total"),
      sortable: true,
    },
    {
      field: "two_total",
      header: "৳2",
      body: currencyBodyTemplate("two_total"),
      sortable: true,
    },
    {
      field: "one_total",
      header: "৳1",
      body: currencyBodyTemplate("one_total"),
      sortable: true,
    },
    {
      field: "suspense",
      header: "Suspense",
      body: suspenseBodyTemplate,
      sortable: true,
    },
    {
      field: "total_amount",
      header: "Total Amount",
      body: totalBodyTemplate,
      sortable: true,
    },
  ];

  return (
    <div>
      <IsLogin></IsLogin>
      {/* <LoaderComponent /> */}
      <style>
        {`
          .cash-count-page {
            background-color: #f8f9fa;
          }
          .summary-card {
            transition: transform 0.2s ease-in-out;
            border: none;
            border-radius: 10px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          }
          .summary-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 20px rgba(0,0,0,0.15);
          }
          .denomination-badge {
            font-weight: 600;
            padding: 8px 12px;
            border-radius: 20px;
          }
          .cash-count-table {
            background: white;
            border-radius: 10px;
            overflow: hidden;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          }
          .page-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 20px;
            border-radius: 10px;
            margin-bottom: 20px;
          }
          .opacity-75 {
            opacity: 0.75;
          }
        `}
      </style>
      <div className="cash-count-page">
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <div>
              <h5 className="am-title mb-1">Cash Count Management</h5>
              <p className="text-muted mb-0">
                Track and manage petty cash counts with detailed denomination
                breakdown
              </p>
            </div>
            <div className="d-flex gap-2">
              <Link to="/create-cash-count" className="btn btn-primary">
                <i className="fa fa-plus-square mg-r-6"></i>New Cash Count
              </Link>
              <button
                className="btn btn-outline-secondary"
                onClick={() => window.location.reload()}
                title="Refresh Data"
              >
                <i className="fa fa-refresh"></i>
              </button>
            </div>
          </div>
          <div className="am-pagebody">
            <Alert alert={alert} />

            {/* Summary Cards */}
            {receiveData && receiveData.length > 0 && (
              <div className="row mb-4">
                <div className="col-md-3">
                  <div className="card bg-primary text-white summary-card">
                    <div className="card-body">
                      <div className="d-flex justify-content-between">
                        <div>
                          <h6 className="card-title mb-0">Total Records</h6>
                          <h4 className="mb-0">{receiveData.length}</h4>
                        </div>
                        <div className="align-self-center">
                          <i className="fa fa-list fa-2x opacity-75"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card bg-success text-white summary-card">
                    <div className="card-body">
                      <div className="d-flex justify-content-between">
                        <div>
                          <h6 className="card-title mb-0">Total Amount</h6>
                          <h4 className="mb-0">
                            ৳{" "}
                            {receiveData
                              .reduce(
                                (sum, item) =>
                                  sum + parseFloat(item.total_amount || 0),
                                0
                              )
                              .toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                              })}
                          </h4>
                        </div>
                        <div className="align-self-center">
                          <i className="fa fa-money fa-2x opacity-75"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card bg-warning text-white summary-card">
                    <div className="card-body">
                      <div className="d-flex justify-content-between">
                        <div>
                          <h6 className="card-title mb-0">Total Suspense</h6>
                          <h4 className="mb-0">
                            ৳{" "}
                            {receiveData
                              .reduce(
                                (sum, item) =>
                                  sum + parseFloat(item.suspense || 0),
                                0
                              )
                              .toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                              })}
                          </h4>
                        </div>
                        <div className="align-self-center">
                          <i className="fa fa-exclamation-triangle fa-2x opacity-75"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card bg-info text-white summary-card">
                    <div className="card-body">
                      <div className="d-flex justify-content-between">
                        <div>
                          <h6 className="card-title mb-0">Average Amount</h6>
                          <h4 className="mb-0">
                            ৳{" "}
                            {(
                              receiveData.reduce(
                                (sum, item) =>
                                  sum + parseFloat(item.total_amount || 0),
                                0
                              ) / receiveData.length
                            ).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                            })}
                          </h4>
                        </div>
                        <div className="align-self-center">
                          <i className="fa fa-calculator fa-2x opacity-75"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="card pd-20 pd-sm-40">
              {/* <h6 className="card-body-title mg-sm-b-30">Datatable</h6> */}
              {/* <div className="row">
                <div className="table-responsive p-0 pb-2">
                  <table className="table table-sm table-bordered table-striped align-items-center justify-content-center mb-0 datatable">
                    <thead>
                      <tr>
                        <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                          S/N
                        </th>
                        <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                          Date
                        </th>
                        <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                          1000
                        </th>
                        <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                          500
                        </th>
                        <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                          200
                        </th>
                        <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                          100
                        </th>
                        <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                          50
                        </th>
                        <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                          20
                        </th>
                        <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                          10
                        </th>
                        <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                          5
                        </th>
                        <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                          2
                        </th>
                        <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                          1
                        </th>
                        <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                          Suspense
                        </th>
                        <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                          Total
                        </th>
                        <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                          Action
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {receiveData.map((item, index) => (
                        <tr key={item.id}>
                          <td className="text-xs">{index + 1}</td>
                          <td className="text-xs">{item.date}</td>
                          <td className="text-xs">{item.thousand_total}</td>
                          <td className="text-xs">{item.five_hundred_total}</td>
                          <td className="text-xs">{item.two_hundred_total}</td>
                          <td className="text-xs">{item.one_hundred_total}</td>
                          <td className="text-xs">{item.fifty_total}</td>
                          <td className="text-xs">{item.twenty_total}</td>
                          <td className="text-xs">{item.ten_total}</td>
                          <td className="text-xs">{item.five_total}</td>
                          <td className="text-xs">{item.two_total}</td>
                          <td className="text-xs">{item.one_total}</td>
                          <td className="text-xs">{item.suspense}</td>
                          <td className="text-xs">{item.total_amount}</td>
                          <td className="text-xs">
                            <Link to={{ pathname: "/edit-cash-count/" + item.id }} className="btn btn-primary btn-sm">
                              <i className="fa fa-pencil" aria-hidden="true"></i>
                            </Link>
                            <button
                              className="btn btn-danger btn-sm ms-1"
                              onClick={() =>
                                deleteData(item.id, "transaction", "cash-count")
                              }
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div> */}
              <div className="cash-count-table">
                <ReactDataTable
                  cols={cols}
                  receiveData={receiveData}
                  isLoading={loading}
                  actionTemplate={actionTemplate}
                  deleteData={deleteData}
                  disablePDF={false}
                  disableCSV={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default List;
